<template>
  <div class="border-top p-2 d-flex align-items-end rs-bg-content">
    <div class="mr-2">
      <label>Width</label>
      <div class="input-group input-group-sm">
        <input
          v-model.number="width"
          type="number"
          class="form-control"
          @input="handleWidthChange(($event.target as HTMLInputElement).value)"
        />

        <div class="input-group-append">
          <button
            type="button"
            class="btn"
            :class="imageWidthUoM === 'px' ? 'btn-secondary' : 'btn-outline-secondary'"
            title="Image width in pixels"
            @click="handleWidthUoMChange('px')"
          >
            px
          </button>
          <button
            type="button"
            class="btn"
            :class="imageWidthUoM === '%' ? 'btn-secondary' : 'btn-outline-secondary'"
            title="Image width in percentage"
            @click="handleWidthUoMChange('%')"
          >
            &percnt;
          </button>
        </div>
      </div>
    </div>

    <div class="mr-2">
      <label>Alt Text</label>
      <input
        class="form-control form-control-sm"
        :value="alt"
        @input="onAltInput"
      />
    </div>

    <div>
      <label>Align</label>
      <select
        v-model="align"
        class="custom-select custom-select-sm"
        @change="handleChange('align', ($event.target as HTMLSelectElement).value)"
      >
        <option v-for="option in alignOptions" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { Editor } from '@tiptap/vue-3';
import { SetImageOptions } from '@tiptap/extension-image';
import { alignOptions } from '@/shared/tiptap/consts';
import { convertStyleObjectToString, convertStyleStringToObject } from '@/shared/tiptap/helpers';
import { TEditableImage } from '@/shared/tiptap/types';

export default defineComponent({
  props: {
    /**
     * The editor instance shared by toolbar and form parent components
     */
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },
  },

  data() {
    return {
      alignOptions,
      width: null as number | null,
      imageWidthUoM: '%',
      align: '',
      alt: '',
      altDebounceTimer: undefined as number | undefined,
      pendingAlt: null as string | null,
      imagePos: null as number | null,
    };
  },

  watch: {
    'editor.state.selection': {
      handler() {
        if (this.editor.isFocused && this.editor.isActive('image')) {
          // A pending edit belongs to the image captured in `imagePos`, not to the one
          // being selected now, so it has to land before the state is re-read.
          this.flushAlt();
          this.setInitialState();
        }
      },
    },
  },

  mounted() {
    this.setInitialState();
  },

  beforeUnmount() {
    // This bar unmounts the moment the image is deselected, which is exactly how a user
    // leaves the Alt Text field. Flush the pending edit rather than dropping it.
    this.flushAlt();
  },

  methods: {
    getStyles() {
      const attrs = this.editor.getAttributes('image');
      return convertStyleStringToObject(attrs.style ?? '');
    },

    setInitialState() {
      const attrs = this.editor.getAttributes('image');
      const styles = this.getStyles();

      // An image can carry no inline width, or a non-numeric one (`auto`, `inherit`). `Number()`
      // turns both into NaN, which `<input type="number">` rejects with a console warning and
      // which defeats the `?? 100` fallback below (NaN is not nullish).
      const parsedWidth = Number(styles.width?.replace(/%|px/, ''));

      this.width = Number.isFinite(parsedWidth) ? parsedWidth : null;
      this.imageWidthUoM = styles.width?.includes('%') ? '%' : 'px';
      this.align = attrs.align;
      this.alt = attrs.alt;
      this.imagePos = this.findSelectedImagePos();
    },

    /**
     * Document position of the image this bar is editing, or `null` when the selection holds no
     * image. Captured up front because a debounced alt edit can be written after the selection
     * has already moved off that image.
     */
    findSelectedImagePos(): number | null {
      const { from, to } = this.editor.state.selection;
      let imagePos: number | null = null;

      this.editor.state.doc.nodesBetween(from, to, (node, pos) => {
        if (imagePos === null && node.type.name === 'image') {
          imagePos = pos;
        }
      });

      return imagePos;
    },

    /**
     * When attribute `key`'s `value` has changed, update it on the editor and emit it via `@input`
     * @param key
     * @param content
     */
    handleChange(key: keyof TEditableImage, content: string) {
      const attrs = this.editor.getAttributes('image') as SetImageOptions;
      const newValue = { ...attrs, [key]: content };

      this.editor
        .chain()
        .updateAttributes('image', newValue)
        .run();
    },

    /**
     * When width changes, converts the styles computed object and stitches in the new width, calls `handleChange`
     */
    handleWidthChange(newVal: string) {
      const styles = this.getStyles();

      this.handleChange(
        'style',
        convertStyleObjectToString({ ...styles, width: `${newVal}${this.imageWidthUoM}` }),
      );
    },

    /**
     * Side effects to set width state if width's unit of measure changes (options are `'px' | '%'`)
     */
    handleWidthUoMChange(newVal: '%' | 'px') {
      this.imageWidthUoM = newVal;

      // Nothing to re-express until the image has a width of its own; writing one here would
      // stamp `width: nullpx` onto the style attribute.
      if (this.width === null) {
        return;
      }

      // If width is greater than 100px currently, set it to 100%.
      if (newVal === '%' && this.width > 100) {
        this.handleWidthChange('100');
        return;
      }

      this.handleWidthChange(`${this.width}`);
    },

    /**
     * Debounces updates to `alt` text to mirror the previous `BFormInput` `:debounce="400"` behavior,
     * parking the typed value in `pendingAlt` until `flushAlt` writes it to the editor.
     */
    onAltInput(e: Event) {
      this.pendingAlt = (e.target as HTMLInputElement).value;

      window.clearTimeout(this.altDebounceTimer);
      this.altDebounceTimer = window.setTimeout(() => this.flushAlt(), 400);
    },

    /**
     * Writes any pending alt text to the image recorded in `imagePos`, then clears the debounce.
     *
     * Unlike `handleChange`, this addresses the image by position instead of going through
     * `updateAttributes`, which acts on the current selection: the flush also runs from
     * `beforeUnmount`, and by then the image has been deselected — that deselection is what
     * unmounted this bar.
     */
    flushAlt() {
      window.clearTimeout(this.altDebounceTimer);
      this.altDebounceTimer = undefined;

      if (this.pendingAlt === null || this.imagePos === null) {
        return;
      }

      const value = this.pendingAlt;
      this.pendingAlt = null;

      // The image can be gone by flush time (deleted while the debounce was pending).
      if (this.editor.state.doc.nodeAt(this.imagePos)?.type.name !== 'image') {
        return;
      }

      this.alt = value;
      this.editor.view.dispatch(this.editor.state.tr.setNodeAttribute(this.imagePos, 'alt', value));
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
</style>
